import { transformers } from "@/configs/workspace";
import type { Transformer } from "@/types/workspace";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ActionIcon, Card, Flex, Group, Menu, Text } from "@mantine/core";
import { IconCancel, IconDotsVertical } from "@tabler/icons-react";
import { useMemo } from "react";
import {
  TransformerCalculate,
  TransformerColumnName,
  TransformerColumnType,
  TransformerFillMissing,
  TransformerFindReplace,
  TransformerGroup,
  TransformerJoin,
  TransformerSelect,
  TransformerSort,
  TransformerUnknown,
  TransformerUpperLower,
  TransformerWhere,
  TransformerWhereSql,
} from "./types";
import "./WorkspaceSidebarFlowTransformer.css";

const transformerComponents: Record<string, React.ComponentType<any>> = {
  select: TransformerSelect,
  calculate: TransformerCalculate,
  where: TransformerWhere,
  where_sql: TransformerWhereSql,
  sort: TransformerSort,
  column_type: TransformerColumnType,
  column_name: TransformerColumnName,
  fill_missing: TransformerFillMissing,
  find_replace: TransformerFindReplace,
  upper_lower: TransformerUpperLower,
  group: TransformerGroup,
  join: TransformerJoin,
};

export function WorkspaceSidebarFlowTransformer({
  transformer,
  onRemove,
  index,
  onUpdate,
}: {
  transformer: Transformer;
  onRemove?: (index: number) => void;
  index: number;
  onUpdate?: (index: number, transformer: Transformer) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `${transformer.type}-${index}`,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition: isDragging || !transform ? undefined : transition,
    zIndex: isDragging ? 1000 : undefined,
  };

  const transformerContent = useMemo(() => {
    const { label, icon } =
      transformers[transformer.type] || transformers.default;
    const Component =
      transformerComponents[transformer.type] || TransformerUnknown;

    return {
      label,
      icon,
      component: (
        <Component
          data={transformer}
          index={index}
          onUpdate={(updatedData: any) =>
            onUpdate?.(index, updatedData as Transformer)
          }
        />
      ),
    };
  }, [transformer, onUpdate, index]);

  const Icon = transformerContent.icon;

  return (
    <Card
      ref={setNodeRef}
      style={style}
      p={0}
      bg="white"
      withBorder
      className="transformer-card"
      shadow="0"
    >
      <Flex direction="column" pb={8}>
        <Flex justify="space-between" align="center">
          <Flex
            p="sm"
            gap={8}
            {...attributes}
            {...listeners}
            style={{
              cursor: "grab",
              flex: 1,
            }}
          >
            <Group gap={8}>
              <Icon size={22} stroke={1} />
              <Text fz={14} fw={500}>
                {transformerContent.label}
              </Text>
            </Group>
          </Flex>
          {onRemove && (
            <Flex px={6}>
              <Menu width={200} position="left-start">
                <Menu.Target>
                  <ActionIcon size="lg" variant="subtle" color="gray.6">
                    <IconDotsVertical size={16} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>{transformerContent.label}</Menu.Label>
                  <Menu.Item
                    c="red"
                    color="red"
                    leftSection={<IconCancel size={16} stroke={1.5} />}
                    onClick={() => onRemove?.(index)}
                  >
                    Remove
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Flex>
          )}
        </Flex>
        {transformerContent?.component}
      </Flex>
    </Card>
  );
}
